import type { Directive, DirectiveBinding } from 'vue'

// A controlled hover tooltip we own — replaces native `title=""` so we can tune
// the delay, styling, and positioning instead of deferring to the OS. Usage:
//   <button v-tooltip="'Move to…'">          // default top placement
//   <button v-tooltip:bottom="'Delete'">     // explicit placement
//   <button v-tooltip="{ text: 'Share', delay: 800 }">
//
// A single tooltip node is shared across the whole app (created lazily, reused),
// so adding the directive to hundreds of buttons costs one DOM node, not hundreds.

type Placement = 'top' | 'bottom' | 'left' | 'right'

interface TooltipOptions {
    text: string
    delay?: number
    placement?: Placement
}

type TipEl = HTMLElement & {
    __tip?: TooltipOptions
    __tipHandlers?: { enter: () => void; leave: () => void; down: () => void }
}

const DEFAULT_DELAY = 500 // ms — long enough to not fire on a passing cursor
const GAP = 8 // px between the element and the tooltip
const VIEWPORT_PAD = 6 // px min distance from the viewport edge

let tipEl: HTMLElement | null = null
let activeEl: HTMLElement | null = null
let showTimer: ReturnType<typeof setTimeout> | null = null

function ensureTip(): HTMLElement {
    if (tipEl) return tipEl
    const el = document.createElement('div')
    el.className = 'c-tooltip'
    el.setAttribute('role', 'tooltip')
    document.body.appendChild(el)
    tipEl = el
    return el
}

function clearTimer() {
    if (showTimer) {
        clearTimeout(showTimer)
        showTimer = null
    }
}

function hide() {
    clearTimer()
    activeEl = null
    if (tipEl) tipEl.classList.remove('is-visible')
}

function position(el: HTMLElement, tip: HTMLElement, placement: Placement) {
    const r = el.getBoundingClientRect()
    const tw = tip.offsetWidth
    const th = tip.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight

    // Auto-flip vertical placements when there isn't room.
    let place = placement
    if (place === 'top' && r.top - th - GAP < VIEWPORT_PAD) place = 'bottom'
    else if (place === 'bottom' && r.bottom + th + GAP > vh - VIEWPORT_PAD) place = 'top'

    let top = 0
    let left = 0
    if (place === 'top' || place === 'bottom') {
        left = r.left + r.width / 2 - tw / 2
        top = place === 'top' ? r.top - th - GAP : r.bottom + GAP
    } else {
        top = r.top + r.height / 2 - th / 2
        left = place === 'left' ? r.left - tw - GAP : r.right + GAP
    }

    // Clamp inside the viewport.
    left = Math.max(VIEWPORT_PAD, Math.min(left, vw - tw - VIEWPORT_PAD))
    top = Math.max(VIEWPORT_PAD, Math.min(top, vh - th - VIEWPORT_PAD))

    tip.dataset.placement = place
    tip.style.left = `${Math.round(left)}px`
    tip.style.top = `${Math.round(top)}px`
}

function show(el: TipEl) {
    const opts = el.__tip
    if (!opts?.text) return
    const tip = ensureTip()
    activeEl = el
    tip.textContent = opts.text
    position(el, tip, opts.placement ?? 'top') // size first, then place
    tip.classList.add('is-visible')
}

function parse(binding: DirectiveBinding): TooltipOptions {
    const arg = binding.arg as Placement | undefined
    if (typeof binding.value === 'string') {
        return { text: binding.value, placement: arg }
    }
    const v = (binding.value ?? {}) as Partial<TooltipOptions>
    return { text: v.text ?? '', delay: v.delay, placement: v.placement ?? arg }
}

const tooltip: Directive<TipEl, string | Partial<TooltipOptions>> = {
    mounted(el, binding) {
        el.__tip = parse(binding)

        const enter = () => {
            clearTimer()
            showTimer = setTimeout(() => show(el), el.__tip?.delay ?? DEFAULT_DELAY)
        }
        const leave = () => {
            if (activeEl === el || showTimer) hide()
        }
        const down = () => hide() // click / drag — the action speaks for itself

        el.__tipHandlers = { enter, leave, down }
        el.addEventListener('mouseenter', enter)
        el.addEventListener('mouseleave', leave)
        el.addEventListener('focusin', enter)
        el.addEventListener('focusout', leave)
        el.addEventListener('mousedown', down)
        el.addEventListener('dragstart', down)
    },
    updated(el, binding) {
        // Re-read so reactive text (e.g. i18n) stays current.
        el.__tip = parse(binding)
        if (activeEl === el && tipEl) tipEl.textContent = el.__tip.text
    },
    beforeUnmount(el) {
        const h = el.__tipHandlers
        if (h) {
            el.removeEventListener('mouseenter', h.enter)
            el.removeEventListener('mouseleave', h.leave)
            el.removeEventListener('focusin', h.enter)
            el.removeEventListener('focusout', h.leave)
            el.removeEventListener('mousedown', h.down)
            el.removeEventListener('dragstart', h.down)
        }
        if (activeEl === el) hide()
    },
}

// Hide on scroll / resize anywhere — a stale fixed-position tooltip looks broken.
if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => hide(), true)
    window.addEventListener('resize', () => hide())
}

export default tooltip
